import { useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ArrowsMerge, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const addr = (p) => [p.address_line, p.town_city, p.county, p.postcode].filter(Boolean).join(", ");
const counts = (p) => `${p.request_count || 0} requests · ${p.job_count || 0} jobs${p.visit_count ? ` · ${p.visit_count} visits` : ""} · ${p.emergency_count || 0} emergencies`;

/** Admin: fold `source` (the duplicate) into another property in the same portfolio. onMerged(report) after success. */
export default function ClientPropertyMergeDialog({ clientId, source, properties, defaultKeepId, onMerged, onClose }) {
  const [keepId, setKeepId] = useState(defaultKeepId && defaultKeepId !== source.id ? defaultKeepId : "");
  const [busy, setBusy] = useState(false);
  const others = (properties || []).filter((p) => p.id !== source.id).sort((a, b) => (b.id === defaultKeepId) - (a.id === defaultKeepId));
  const keep = others.find((p) => p.id === keepId);

  const merge = async () => {
    if (!keep) return;
    setBusy(true);
    try {
      const { data } = await API.post(`/clients/${clientId}/properties/${keep.id}/merge`, { merge_ids: [source.id] });
      toast.success(`Merged "${source.name}" into "${keep.name}" · ${data.total_moved} record${data.total_moved === 1 ? "" : "s"} moved`);
      onMerged?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Merge failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="client-property-merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowsMerge size={18} className="text-primary" /> Merge duplicate property</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="border border-red-200 bg-red-500/5 rounded-md p-3" data-testid="merge-source">
            <p className="text-[10px] uppercase tracking-wide text-red-700 font-semibold">Will be retired</p>
            <p className="font-medium">{source.name}</p>
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin size={11} /> {addr(source) || "No address"}</p>
            <p className="text-[10px] text-muted-foreground">{counts(source)}</p>
          </div>
          <p className="text-xs text-muted-foreground">Everything linked to it — requests, jobs, contract visits, emergencies and projects — moves to the property you keep. Any blank contact or access fields on the kept card are filled from this one. This can't be undone.</p>
          <div>
            <p className="text-xs font-medium mb-1.5">Keep and merge into…</p>
            {others.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="merge-no-targets">No other properties in this portfolio to merge into.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1" data-testid="merge-targets">
                {others.map((p) => (
                  <label key={p.id} className={`flex items-start gap-2 border rounded-md p-2.5 cursor-pointer transition-colors ${keepId === p.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`} data-testid={`merge-target-${p.id}`}>
                    <input type="radio" name="merge-keep" className="mt-0.5" checked={keepId === p.id} onChange={() => setKeepId(p.id)} data-testid={`merge-target-radio-${p.id}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium truncate">{p.name}{p.id === defaultKeepId && <span className="ml-1.5 text-[10px] font-semibold text-emerald-700 bg-emerald-500/10 border border-emerald-200 rounded px-1 py-px align-middle" data-testid="merge-suggested-badge">Suggested</span>}</span>
                      <span className="block text-xs text-muted-foreground truncate">{addr(p) || "No address"}</span>
                      <span className="block text-[10px] text-muted-foreground">{counts(p)}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="merge-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={merge} disabled={!keep || busy} data-testid="merge-confirm-btn">
              <ArrowsMerge size={14} className="mr-1" /> {busy ? "Merging…" : keep ? `Merge into "${keep.name}"` : "Merge"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
